import { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import ErrorMessage from "./ErrorMessage";
import { CheckMail } from "asset/icons";
import Loader from "component/loader";
import { handleSendVerificationEmail } from "api/fetchers";
import { useDispatch } from "react-redux";
import fetchUserLocation from "utilities/fetchUserLocation";
import { LOGIN_USER } from "store/action";
import hasLoggedInWithinLastMonth from "utilities/hasLoggedInWithinLastMonth";

const EmailVerification = () => {
    const [code, setCode] = useState(["", "", "", ""]);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [resendTimer, setResendTimer] = useState(120);
    const dispatch = useDispatch();
    const [deviceType, setDeviceType] = useState("");
    const [location, setLocation] = useState(null);
    const [sendAlert, setSendAlert] = useState(false);

    const inputRefs = useRef([]);
    const navigate = useNavigate();
    const { state } = useLocation();
    const email = state?.email;

    useEffect(() => {
        if (resendTimer > 0) {
            const interval = setInterval(() => {
                setResendTimer((prev) => prev - 1);
            }, 1000);
            return () => clearInterval(interval);
        }
    }, [resendTimer]);

    useEffect(() => {
        setDeviceType(navigator.userAgent);
        fetchUserLocation().then((loc) => setLocation(loc));
        setSendAlert(!hasLoggedInWithinLastMonth());
    }, []);

    useEffect(() => {
        if (!email) {
            navigate("/auth/recover-password");
        }
    }, [email, navigate]);

    const handleChange = (index, value) => {
        if (/^[0-9]?$/.test(value)) {
            const newCode = [...code];
            newCode[index] = value;
            setCode(newCode);

            if (value && index < 3) {
                inputRefs.current[index + 1]?.focus();
            }
        }
    };

    const handleKeyDown = (index, e) => {
        if (e.key === "Backspace" && !code[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handleSubmit = async () => {
        setLoading(true);
        setError("");

        const joinedCode = code.join("");

        try {
            if (state?.flow === "recover") {
                await axios.post("/v1/users/confirm-forgot-password-token", {
                    email,
                    code: joinedCode,
                });

                navigate("/auth/create-new-password", {
                    state: {
                        email,
                        code: joinedCode,
                    },
                });
            } else if (state?.flow === "signup") {
                const payload = {
                    email,
                    password: state.password,
                    firstName: state.firstName,
                    lastName: state.lastName,
                    phoneNumber: state.phoneNumber,
                    code: joinedCode,
                };

                const res = await axios.post("/v1/users/register", payload);

                // Log the user in automatically
                const loginPayload = {
                    email: state.email,
                    password: state.password,
                    sendAlert: sendAlert.toString(),
                    device: deviceType,
                    location,
                };

                const loginRes = await LOGIN_USER(loginPayload, dispatch);
                sessionStorage.setItem("lastLoginTimestamp", new Date().getTime());

                const user = loginRes?.data?.data?.user;

                if (user?.pinStatus === "NOT_VERIFIED") {
                    return window.location.replace("/auth/setup-pin");
                }

                window.location.replace("/dashboard");
            }
        } catch (err) {
            setError(err?.response?.data?.message || "Invalid or expired code.");
        } finally {
            setLoading(false);
        }
    };

    const resendCode = async () => {
        try {
            setLoading(true);

            if (state?.flow === "signup") {
                await handleSendVerificationEmail(email, state.firstName, state.phoneNumber);
            } else if (state?.flow === "recover") {
                await axios.post("/v1/users/send-forgot-password", { email });
            }

            // setError("Verification code resent successfully.");
            setResendTimer(120);
        } catch (err) {
            setError("Failed to resend verification code.");
        } finally {
            setLoading(false);
        }
    };

    const formatTime = (seconds) => {
        const min = String(Math.floor(seconds / 60)).padStart(2, "0");
        const sec = String(seconds % 60).padStart(2, "0");
        return `${min}:${sec}`;
    };

    const changeEmailPath = state?.flow === "signup" ? "/auth/signup" : "/auth/recover-password";

    return (
        <div className="bg-white rounded-xl w-[600px] mx-auto border border-[#EBEDF9]">
            <div className="flex !pt-5">
                <CheckMail />
            </div>
            <div className="p-8 !pt-0">
                <h2 className="text-2xl font-bold text-gray-900">Check your email for a code</h2>
                <p className="text-gray-600 text-sm mt-2">
                    Enter the 4-digit code sent to{" "}
                    <span className="font-semibold text-gray-900">{email}</span>
                    <span
                        className="text-[#6F00FF] cursor-pointer ml-1 hover:underline"
                        onClick={() => navigate(changeEmailPath)}
                    >
                        Change email
                    </span>
                </p>

                <div className="flex gap-3 my-6">
                    {code.map((digit, index) => (
                        <input
                            key={index}
                            ref={(el) => (inputRefs.current[index] = el)}
                            type="text"
                            maxLength="1"
                            value={digit}
                            onChange={(e) => handleChange(index, e.target.value)}
                            onKeyDown={(e) => handleKeyDown(index, e)}
                            className="w-12 h-12 text-2xl text-center border border-gray-300 rounded-md focus:border-[#6F00FF] focus:ring-2 focus:ring-[#6F00FF] transition"
                        />
                    ))}
                </div>

                <ErrorMessage message={error} />

                <div className="flex justify-between items-center">
                    <p className="text-sm text-gray-500 !mb-0">
                        Didn’t receive code? Check spam or{" "}
                        {resendTimer > 0 ? (
                            <span className="text-gray-400 font-medium">
                                Resend in {formatTime(resendTimer)}
                            </span>
                        ) : (
                            <span
                                className="text-[#6F00FF] cursor-pointer ml-1 hover:underline"
                                onClick={resendCode}
                            >
                                Resend code
                            </span>
                        )}
                    </p>

                    <button
                        onClick={handleSubmit}
                        disabled={loading}
                        className={`bg-[#6F00FF] text-white py-2 px-4 text-sm rounded-lg hover:bg-[#6f00fff0] transition ${
                            loading ? "opacity-50 cursor-not-allowed" : ""
                        }`}
                    >
                        {loading ? <Loader /> : "Continue"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EmailVerification;
